import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ComposedChart,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  RefreshCw,
  Thermometer,
  Droplets,
  AlertCircle,
  Zap,
  Activity,
  Trophy,
  Leaf,
  ArrowDown,
  ArrowUp,
  Wind,
  Lightbulb,
  Settings2,
  Info
} from 'lucide-react';
import PhaseGauge from '../PhaseGauge';

// Chart constants
const CHART_MARGIN = { top: 10, right: 10, bottom: 0, left: 0 };

// Custom Tooltip that filters out duplicate dataKeys (from Area components)
const CustomPowerTooltip = ({ active, payload, label, todayLabel, yesterdayLabel, lastWeekLabel }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  
  // Filter out duplicates - only keep unique dataKeys (Area and Line share same dataKey)
  const seen = new Set<string>();
  const filteredPayload = payload.filter((entry: any) => {
    if (seen.has(entry.dataKey)) return false;
    seen.add(entry.dataKey);
    return true;
  });
  
  const formatValue = (value: number | null) => {
    if (value === null || value === undefined) return '--';
    return value >= 1000 ? `${(value / 1000).toFixed(2)} kWh` : `${value.toFixed(2)} kWh`;
  };
  
  const getLabel = (dataKey: string) => {
    if (dataKey === 'powerPast' || dataKey === 'power') return todayLabel;
    if (dataKey === 'yesterday') return yesterdayLabel;
    if (dataKey === 'lastWeek') return lastWeekLabel;
    return dataKey;
  };
  
  const getColor = (dataKey: string) => {
    if (dataKey === 'powerPast' || dataKey === 'power') return '#eab308';
    if (dataKey === 'yesterday') return '#9CA3AF';
    if (dataKey === 'lastWeek') return '#60A5FA';
    return '#fff';
  };
  
  return (
    <div className="bg-[#1F2937] border border-[#374151] rounded-lg p-3">
      <p className="text-slate-400 text-sm mb-2">{label}</p>
      {filteredPayload.map((entry: any, index: number) => (
        <p key={index} style={{ color: getColor(entry.dataKey) }} className="text-sm">
          {getLabel(entry.dataKey)} : {formatValue(entry.value)}
        </p>
      ))}
    </div>
  );
};

const CustomMonthlyTooltip = ({ active, payload, label, currentMonthLabel, lastMonthLabel }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  
  // Filter out duplicates
  const seen = new Set<string>();
  const filteredPayload = payload.filter((entry: any) => {
    if (seen.has(entry.dataKey)) return false;
    seen.add(entry.dataKey);
    return true;
  });
  
  const formatValue = (value: number | null) => {
    if (value === null || value === undefined) return '--';
    return value >= 1000 ? `${(value / 1000).toFixed(2)} kWh` : `${value.toFixed(2)} kWh`;
  };
  
  const getLabel = (dataKey: string) => {
    if (dataKey === 'powerPast' || dataKey === 'powerFuture' || dataKey === 'power') return currentMonthLabel;
    if (dataKey === 'lastMonth') return lastMonthLabel;
    return dataKey;
  };
  
  const getColor = (dataKey: string) => {
    if (dataKey === 'powerPast' || dataKey === 'powerFuture' || dataKey === 'power') return '#eab308';
    if (dataKey === 'lastMonth') return '#9CA3AF';
    return '#fff';
  };
  
  return (
    <div className="bg-[#1F2937] border border-[#374151] rounded-lg p-3">
      <p className="text-slate-400 text-sm mb-2">{label}</p>
      {filteredPayload.map((entry: any, index: number) => (
        <p key={index} style={{ color: getColor(entry.dataKey) }} className="text-sm">
          {getLabel(entry.dataKey)} : {formatValue(entry.value)}
        </p>
      ))}
    </div>
  );
};

const CustomClimateTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  
  // Filter out duplicates
  const seen = new Set<string>();
  const filteredPayload = payload.filter((entry: any) => {
    if (seen.has(entry.dataKey)) return false;
    seen.add(entry.dataKey);
    return true;
  });
  
  return (
    <div className="bg-[#1F2937] border border-[#374151] rounded-lg p-3">
      <p className="text-slate-400 text-sm mb-2">{label}</p>
      {filteredPayload.map((entry: any, index: number) => (
        <p key={index} style={{ color: entry.dataKey === 'temp' ? '#F97316' : '#60A5FA' }} className="text-sm">
          {entry.dataKey === 'temp' ? '溫度' : '濕度'} : {entry.dataKey === 'temp' ? `${Number(entry.value).toFixed(1)}°C` : `${Number(entry.value).toFixed(0)}%`}
        </p>
      ))}
    </div>
  );
};

// Types
interface IntelliSpaceDevice {
  deviceId: string;
  baseSN: string;
  deviceName: string;
  nickname?: string;
  originalName?: string;
  systemDeviceName?: string;
  deviceType: string;
  status: 'online' | 'offline';
  location?: string;
  groupId?: string;
  groupName?: string;
  traits?: string[];
  attributes?: any;
  watt?: number;
  wattHour?: number;
  voltage?: number;
  current?: number;
  temperature?: number;
  humidity?: number;
  co2?: number;
  tvoc?: number;
  pm25?: number;
  presence?: boolean;
  occupancy?: boolean;
  luxValue?: number;
  lux?: number;
  isOn?: boolean;
  mode?: string;
  thermostatMode?: string;
  fanSpeed?: string;
  setTemperature?: number;
  thermostatTemperatureSetpoint?: number;
  power?: boolean;
  brightness?: number;
  colorTemp?: number;
  colorTemperature?: number;
  switchOn?: boolean;
  openPercent?: number;
  isOpen?: boolean;
  batteryLevel?: number;
  energy?: number;
  powerFactor?: number;
}

interface Stats {
  totalPower: number;
  hvacPower: number;
  lightingPower: number;
  otherPower: number;
  hvacMeterCount: number;
  lightingMeterCount: number;
  powerMeterCount: number;
  thSensorCount: number;
  avgTemp?: number;
  avgHumidity?: number;
}

interface PowerChartDataPoint {
  hour: string;
  power: number;
  powerPast: number | null;
  powerFuture: number | null;
  yesterday: number;
  lastWeek: number;
}

interface MonthlyChartDataPoint {
  day: string;
  power: number;
  powerPast: number | null;
  powerFuture: number | null;
  lastMonth: number;
}

interface ClimateChartDataPoint {
  hour: string;
  temp: number | null;
  humidity: number | null;
}

interface HourlyPowerDataPoint {
  hour: string;
  power: number;
  energy?: number;
}

export interface EnergyViewProps {
  devices: IntelliSpaceDevice[];
  stats: Stats;
  selectedPowerMeters: string[];
  todayMinuteEnergy: number;
  yesterdayDailyEnergy: number;
  powerChartData: PowerChartDataPoint[];
  monthlyChartData: MonthlyChartDataPoint[];
  climateChartData: ClimateChartDataPoint[];
  hasClimateChartData: boolean;
  hourlyPowerData: HourlyPowerDataPoint[];
  weeklyEnergyPerDevice: Record<string, number>;
  excludedRankingDevices: string[];
  selectedThreePhaseMeter: string | null;
  selectedThSensors: string[];
  isLoadingChartData: boolean;
  isLoadingWeeklyEnergy: boolean;
  isTodayEnergyLoading: boolean;
  isDebugMode: boolean;
  // Callbacks
  openPowerMeterSelector: () => void;
  openHvacMeterSelector: () => void;
  openLightingMeterSelector: () => void;
  openThreePhaseMeterSelector: () => void;
  fetchWeeklyEnergyRanking: () => void;
  setTempExcludedRankingDevices: (devices: string[]) => void;
  setShowExcludeRankingModal: (show: boolean) => void;
  setSelectedDevice: (device: any) => void;
  // Translations
  t: any;
}

// Active Dot Component for charts
const ActiveDot = (props: any) => {
  const { cx, cy, stroke, payload, index, color } = props;
  if (payload.power === null || payload.power === undefined) return null;
  return (
    <circle key={`active-dot-${index}`} cx={cx} cy={cy} r={5} stroke={color} strokeWidth={2} fill={color} />
  );
};

// Power Chart Component
interface PowerChartProps {
  data: PowerChartDataPoint[];
  todayLabel: string;
  yesterdayLabel: string;
  lastWeekLabel: string;
}

const PowerChart = React.memo<PowerChartProps>(({ data, todayLabel, yesterdayLabel, lastWeekLabel }) => (
  <ResponsiveContainer width="100%" height="100%">
    <ComposedChart data={data} margin={CHART_MARGIN}>
      <defs>
        <linearGradient id="todayGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#eab308" stopOpacity={0.4}/>
          <stop offset="95%" stopColor="#eab308" stopOpacity={0.05}/>
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
      <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#9CA3AF' }} stroke="#4B5563" interval="preserveStartEnd" />
      <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} stroke="#4B5563" width={35} domain={[0, 'auto']} tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value} />
      <Tooltip content={<CustomPowerTooltip todayLabel={todayLabel} yesterdayLabel={yesterdayLabel} lastWeekLabel={lastWeekLabel} />} />
      {/* 今日漸層陰影 */}
      <Area type="linear" dataKey="powerPast" stroke="none" fill="url(#todayGradient)" connectNulls={false} />
      {/* 上周：折線 + 實心點 + 虛線 */}
      <Line type="linear" dataKey="lastWeek" stroke="#60A5FA" strokeWidth={1.5} strokeDasharray="5 5" dot={{ r: 4, fill: '#60A5FA', strokeWidth: 0 }} activeDot={{ r: 6 }} />
      {/* 昨日：折線 + 實心點 */}
      <Line type="linear" dataKey="yesterday" stroke="#9CA3AF" strokeWidth={1.5} dot={{ r: 4, fill: '#9CA3AF', strokeWidth: 0 }} activeDot={{ r: 6 }} />
      {/* 今日：折線加粗 + 空心點 */}
      <Line type="linear" dataKey="powerPast" stroke="#eab308" strokeWidth={2.5} dot={{ r: 4, fill: '#0f172a', stroke: '#eab308', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#eab308' }} connectNulls={false} />
    </ComposedChart>
  </ResponsiveContainer>
));

// Monthly Power Chart Component
interface MonthlyPowerChartProps {
  data: MonthlyChartDataPoint[];
  currentMonthLabel: string;
  lastMonthLabel: string;
}

const MonthlyPowerChart = React.memo<MonthlyPowerChartProps>(({ data, currentMonthLabel, lastMonthLabel }) => (
  <ResponsiveContainer width="100%" height="100%">
    <ComposedChart data={data} margin={CHART_MARGIN}>
      <defs>
        <linearGradient id="monthlyGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#eab308" stopOpacity={0.4}/>
          <stop offset="95%" stopColor="#eab308" stopOpacity={0.05}/>
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
      <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9CA3AF' }} stroke="#4B5563" interval="preserveStartEnd" />
      <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} stroke="#4B5563" width={40} domain={[0, 'auto']} tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value} />
      <Tooltip content={<CustomMonthlyTooltip currentMonthLabel={currentMonthLabel} lastMonthLabel={lastMonthLabel} />} />
      {/* 本月漸層陰影 */}
      <Area type="linear" dataKey="powerPast" stroke="none" fill="url(#monthlyGradient)" connectNulls={false} />
      {/* 上月：折線 + 實心點 */}
      <Line type="linear" dataKey="lastMonth" stroke="#9CA3AF" strokeWidth={1.5} dot={{ r: 4, fill: '#9CA3AF', strokeWidth: 0 }} activeDot={{ r: 6 }} />
      {/* 本月（已過日期）：折線加粗 + 空心點 */}
      <Line type="linear" dataKey="powerPast" stroke="#eab308" strokeWidth={2.5} dot={{ r: 4, fill: '#0f172a', stroke: '#eab308', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#eab308' }} connectNulls={false} />
      {/* 本月（未來日期）：折線虛線 + 小點 */}
      <Line type="linear" dataKey="powerFuture" stroke="#eab308" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: '#0f172a', stroke: '#eab308', strokeWidth: 1 }} connectNulls={false} />
    </ComposedChart>
  </ResponsiveContainer>
));

// Climate Chart Component
interface ClimateChartProps {
  data: ClimateChartDataPoint[];
  hasData?: boolean;
  realtimeTemp?: number | null;
  realtimeHumidity?: number | null;
}

const ClimateChart = React.memo<ClimateChartProps>(({ data, hasData = true, realtimeTemp, realtimeHumidity }) => {
  // 計算溫度 Y 軸範圍，確保至少顯示 6°C 範圍，避免視覺放大效應
  const tempDomain = React.useMemo(() => {
    const MIN_RANGE = 6; // 最小顯示 6°C 範圍
    const tempValues = data
      .map(d => d.temp)
      .filter((v): v is number => typeof v === 'number' && !isNaN(v));
    
    if (tempValues.length === 0) {
      return [18, 28] as [number, number]; // 預設舒適溫度範圍
    }
    
    const dataMin = Math.min(...tempValues);
    const dataMax = Math.max(...tempValues);
    const range = dataMax - dataMin;
    
    if (range < MIN_RANGE) {
      // 範圍太小，擴展到至少 MIN_RANGE
      const center = (dataMin + dataMax) / 2;
      return [
        Math.floor(center - MIN_RANGE / 2),
        Math.ceil(center + MIN_RANGE / 2)
      ] as [number, number];
    }
    
    // 範圍足夠，加一點 padding
    return [
      Math.floor(dataMin - 1),
      Math.ceil(dataMax + 1)
    ] as [number, number];
  }, [data]);

  if (!hasData) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-4">
        <div className="text-slate-500 text-sm mb-4">
          無歷史數據
        </div>
        {(realtimeTemp !== undefined && realtimeTemp !== null) || (realtimeHumidity !== undefined && realtimeHumidity !== null) ? (
          <div className="flex gap-8">
            {realtimeTemp !== undefined && realtimeTemp !== null && (
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-500">{realtimeTemp.toFixed(1)}°C</div>
                <div className="text-xs text-slate-400 mt-1">即時溫度</div>
              </div>
            )}
            {realtimeHumidity !== undefined && realtimeHumidity !== null && (
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-500">{realtimeHumidity.toFixed(0)}%</div>
                <div className="text-xs text-slate-400 mt-1">即時濕度</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-slate-600 text-xs">請選擇溫濕度感測器</div>
        )}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={CHART_MARGIN}>
        <defs>
          <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#F97316" stopOpacity={0.02}/>
          </linearGradient>
          <linearGradient id="humidityGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#60A5FA" stopOpacity={0.02}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#9CA3AF' }} stroke="#4B5563" interval="preserveStartEnd" />
        <YAxis yAxisId="temp" orientation="left" tick={{ fontSize: 10, fill: '#9CA3AF' }} stroke="#4B5563" width={30} domain={tempDomain} />
        <YAxis yAxisId="humidity" orientation="right" tick={{ fontSize: 10, fill: '#9CA3AF' }} stroke="#4B5563" width={30} domain={[0, 100]} />
        <Tooltip content={<CustomClimateTooltip />} />
        {/* 濕度漸層陰影 */}
        <Area yAxisId="humidity" type="linear" dataKey="humidity" stroke="none" fill="url(#humidityGradient)" connectNulls />
        {/* 溫度漸層陰影 */}
        <Area yAxisId="temp" type="linear" dataKey="temp" stroke="none" fill="url(#tempGradient)" connectNulls />
        {/* 濕度：折線加粗 + 空心點 */}
        <Line yAxisId="humidity" type="linear" dataKey="humidity" stroke="#60A5FA" strokeWidth={2.5} dot={{ r: 4, fill: '#0f172a', stroke: '#60A5FA', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#60A5FA' }} name="humidity" connectNulls />
        {/* 溫度：折線加粗 + 空心點 */}
        <Line yAxisId="temp" type="linear" dataKey="temp" stroke="#F97316" strokeWidth={2.5} dot={{ r: 4, fill: '#0f172a', stroke: '#F97316', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#F97316' }} name="temp" connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );
});

const EnergyView: React.FC<EnergyViewProps> = ({
  devices,
  stats,
  selectedPowerMeters,
  todayMinuteEnergy,
  yesterdayDailyEnergy,
  powerChartData,
  monthlyChartData,
  climateChartData,
  hasClimateChartData,
  hourlyPowerData,
  weeklyEnergyPerDevice,
  excludedRankingDevices,
  selectedThreePhaseMeter,
  selectedThSensors,
  isLoadingChartData,
  isLoadingWeeklyEnergy,
  isTodayEnergyLoading,
  isDebugMode,
  openPowerMeterSelector,
  openHvacMeterSelector,
  openLightingMeterSelector,
  openThreePhaseMeterSelector,
  fetchWeeklyEnergyRanking,
  setTempExcludedRankingDevices,
  setShowExcludeRankingModal,
  setSelectedDevice,
  t,
}) => {
  // Get three-phase data
  const getThreePhaseData = (device: IntelliSpaceDevice | null | undefined) => {
    if (!device) return null;
    const attrs = device.attributes?.["1"] || device.attributes || {};
    const hasThreePhase = attrs.voltage_r !== undefined || attrs.real_power_r !== undefined;
    if (!hasThreePhase) return null;
    return {
      R: { voltage: attrs.voltage_r ?? 0, current: attrs.current_r ?? 0, power: attrs.real_power_r ?? 0 },
      S: { voltage: attrs.voltage_s ?? 0, current: attrs.current_s ?? 0, power: attrs.real_power_s ?? 0 },
      T: { voltage: attrs.voltage_t ?? 0, current: attrs.current_t ?? 0, power: attrs.real_power_t ?? 0 },
    };
  };

  // Available three-phase meters
  const availableThreePhaseMeters = useMemo(() => devices.filter(d => {
    if (d.deviceType !== 'power_meter' && d.deviceType !== 'power_meter_3phase') return false;
    const attrs = d.attributes?.["1"] || d.attributes || {};
    return attrs.voltage_r !== undefined || attrs.real_power_r !== undefined;
  }), [devices]);

  const hasAvailableMeters = availableThreePhaseMeters.length > 0;
  const threePhaseMeter = selectedThreePhaseMeter 
    ? devices.find(d => d.deviceId === selectedThreePhaseMeter)
    : null;
  const threePhaseData = getThreePhaseData(threePhaseMeter);
  const maxPower = 10000;

  // Energy ranking calculation
  const { top5, weeklyTotalEnergy, allEnergyDevices } = useMemo(() => {
    const allEnergyDevices = devices.filter(d => {
      const hasMeterData = d.watt !== undefined || d.wattHour !== undefined || 
                          d.voltage !== undefined || d.current !== undefined;
      const isPowerMeter = d.deviceType === 'power_meter' || d.deviceType === 'power_meter_3phase';
      const sp = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
      const hasMeterAttributes = sp.watt || sp.watt_hour || sp.voltage || sp.current || sp.real_power;
      return isPowerMeter || hasMeterData || hasMeterAttributes;
    });

    const devicesWithWeeklyEnergy = allEnergyDevices
      .filter(d => {
        if (excludedRankingDevices.includes(d.deviceId)) return false;
        const weeklyEnergy = weeklyEnergyPerDevice[d.deviceId];
        if (weeklyEnergy === undefined || weeklyEnergy === null || weeklyEnergy < 0) return false;
        return true;
      })
      .map(d => ({
        ...d,
        weeklyEnergyKWh: weeklyEnergyPerDevice[d.deviceId] || 0
      }));

    const top5 = [...devicesWithWeeklyEnergy]
      .sort((a, b) => b.weeklyEnergyKWh - a.weeklyEnergyKWh)
      .slice(0, 5);

    const weeklyTotalEnergy = selectedPowerMeters
      .reduce((sum, deviceId) => sum + (weeklyEnergyPerDevice[deviceId] || 0), 0);

    return { top5, weeklyTotalEnergy, allEnergyDevices };
  }, [devices, weeklyEnergyPerDevice, excludedRankingDevices, selectedPowerMeters]);

  // Carbon footprint calculation
  const { carbonFootprint, treeEquivalent } = useMemo(() => {
    const todayEnergy = hourlyPowerData.reduce((sum, d) => sum + (d.energy || 0), 0);
    const CARBON_FACTOR = 0.495;
    const TREE_DAILY_ABSORPTION = 0.033;
    const carbonFootprint = todayEnergy * CARBON_FACTOR;
    const treeEquivalent = Math.round(carbonFootprint / TREE_DAILY_ABSORPTION);
    return { carbonFootprint, treeEquivalent };
  }, [hourlyPowerData]);

  // 圖表統計數據 - 用於顯示變化幅度/最高值
  const chartStats = useMemo(() => {
    // 本日用電 - 今日最高值
    const todayPowerValues = powerChartData
      .map(d => d.powerPast)
      .filter((v): v is number => typeof v === 'number' && v > 0);
    const todayPeakPower = todayPowerValues.length > 0 ? Math.max(...todayPowerValues) : 0;
    
    // 本月用電 - 本月最高日用電
    const monthPowerValues = monthlyChartData
      .map(d => d.powerPast)
      .filter((v): v is number => typeof v === 'number' && v > 0);
    const monthPeakPower = monthPowerValues.length > 0 ? Math.max(...monthPowerValues) : 0;
    
    // 溫濕度 - 溫度變化幅度
    const tempValues = climateChartData
      .map(d => d.temp)
      .filter((v): v is number => typeof v === 'number' && !isNaN(v));
    const tempMin = tempValues.length > 0 ? Math.min(...tempValues) : null;
    const tempMax = tempValues.length > 0 ? Math.max(...tempValues) : null;
    const tempRange = tempMin !== null && tempMax !== null ? tempMax - tempMin : null;
    
    return {
      todayPeakPower,
      monthPeakPower,
      tempMin,
      tempMax,
      tempRange
    };
  }, [powerChartData, monthlyChartData, climateChartData]);

  // 格式化功率顯示（W）
  const formatPowerStats = (watts: number): string => {
    if (watts >= 1000) {
      return `${(watts / 1000).toFixed(1)}k`;
    }
    return watts.toFixed(0);
  };

  // 格式化用電量顯示（Wh → kWh）
  const formatEnergyStats = (wattHours: number): string => {
    if (wattHours >= 1000) {
      return `${(wattHours / 1000).toFixed(2)} kWh`;
    }
    return `${wattHours.toFixed(0)} Wh`;
  };

  return (
    <div className="flex flex-col gap-4 min-h-full">
      {/* Power Breakdown Section */}
      <div className="bg-[#0C2340] rounded-xl shadow-sm border border-slate-700/30 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 min-h-[260px] lg:min-h-[300px] items-stretch auto-rows-fr">
          {/* Today's Total Energy */}
          <div className="flex h-full flex-col items-center justify-center rounded-xl p-4 cursor-pointer" onClick={openPowerMeterSelector}>
            <Zap className="w-10 h-10 text-yellow-500 mb-2" />
            <div className="flex items-center gap-2 mb-1">
              <p className="text-lg font-semibold text-slate-400">今日用電量</p>
              {!isTodayEnergyLoading && yesterdayDailyEnergy > 0 && todayMinuteEnergy > 0 && (() => {
                const percentChange = ((todayMinuteEnergy - yesterdayDailyEnergy) / yesterdayDailyEnergy) * 100;
                const isIncrease = percentChange > 0;
                const absPercent = Math.abs(percentChange);
                return (
                  <span className={`text-base font-semibold flex items-center gap-1 ${isIncrease ? 'text-red-500' : 'text-green-500'}`}>
                    {isIncrease ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                    {absPercent.toFixed(1)}%
                  </span>
                );
              })()}
            </div>
            <p className="text-7xl font-bold text-yellow-500 leading-tight">
              {selectedPowerMeters.length === 0 ? '--' : 
               isTodayEnergyLoading ? (
                 <span className="inline-block animate-pulse bg-slate-700 rounded w-32 h-16"></span>
               ) : todayMinuteEnergy.toFixed(2)}
              <span className="text-4xl font-normal text-slate-400 ml-1">kWh</span>
            </p>
            <p className="text-base text-slate-400 mt-1">
              昨日 <span className="font-bold">{selectedPowerMeters.length > 0 ? yesterdayDailyEnergy.toFixed(2) : '--'}</span> kWh
            </p>
            <p className="text-base text-slate-400 mt-3">
              {selectedPowerMeters.length > 0 ? `${selectedPowerMeters.length} 電錶已選` : '點擊選擇電錶'}
            </p>
          </div>

          {/* HVAC Real-time Power */}
          <div className="flex h-full flex-col items-center justify-center rounded-xl p-4 cursor-pointer" onClick={openHvacMeterSelector}>
            <Wind className="w-12 h-12 text-cyan-500 mb-3" />
            <p className="text-lg font-semibold text-slate-400 mb-1">空調即時用電</p>
            <p className="text-6xl font-bold text-white">
              {stats.hvacMeterCount > 0
                ? (typeof stats.hvacPower === 'number' && stats.hvacPower >= 1000
                    ? `${(stats.hvacPower / 1000).toFixed(2)} kW`
                    : `${(stats.hvacPower || 0).toFixed(0)} W`)
                : '-- W'}
            </p>
            <p className="text-base text-slate-400 mt-2">
              {stats.hvacMeterCount > 0 ? `${stats.hvacMeterCount} 電錶已選` : '點擊選擇電錶'}
            </p>
          </div>

          {/* Lighting Real-time Power */}
          <div className="flex h-full flex-col items-center justify-center rounded-xl p-4 cursor-pointer" onClick={openLightingMeterSelector}>
            <Lightbulb className="w-12 h-12 text-yellow-500 mb-3" />
            <p className="text-lg font-semibold text-slate-400 mb-1">照明即時用電</p>
            <p className="text-6xl font-bold text-white">
              {stats.lightingMeterCount > 0
                ? (typeof stats.lightingPower === 'number' && stats.lightingPower >= 1000
                    ? `${(stats.lightingPower / 1000).toFixed(2)} kW`
                    : `${(stats.lightingPower || 0).toFixed(0)} W`)
                : '-- W'}
            </p>
            <p className="text-base text-slate-400 mt-2">
              {stats.lightingMeterCount > 0 ? `${stats.lightingMeterCount} 電錶已選` : '點擊選擇電錶'}
            </p>
          </div>

          {/* Pie Chart */}
          <div className="flex h-full flex-col items-center justify-center relative [&_*]:outline-none">
            <div className="relative -mt-4" style={{ width: 300, height: 200 }}>
              <PieChart width={300} height={200} margin={{ top: 0, right: 30, bottom: 0, left: 30 }}>
                <Pie
                  data={[
                    { name: '空調用電', value: stats.hvacPower, color: '#06b6d4' },
                    { name: '照明用電', value: stats.lightingPower, color: '#eab308' },
                    { name: '其他用電', value: stats.otherPower, color: '#94a3b8' }
                  ].filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={95}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                  isAnimationActive={false}
                >
                  {[
                    { name: '空調用電', value: stats.hvacPower, color: '#06b6d4' },
                    { name: '照明用電', value: stats.lightingPower, color: '#eab308' },
                    { name: '其他用電', value: stats.otherPower, color: '#94a3b8' }
                  ].filter(d => d.value > 0).map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color}
                      className="hover:opacity-80 transition-opacity cursor-pointer"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip 
                  wrapperStyle={{ zIndex: 100 }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0];
                      const value = data.value as number;
                      const formattedValue = value >= 1000 
                        ? `${(value / 1000).toFixed(2)} kW` 
                        : `${value.toFixed(0)} W`;
                      return (
                        <div className="px-3 py-2 rounded-lg shadow-xl border border-slate-600 relative z-50" style={{ backgroundColor: '#1e293b' }}>
                          <p className="text-sm font-medium text-white">
                            {data.name}: {formattedValue}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
              {/* Center Label */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {(() => {
                  const selectedPower = selectedPowerMeters.length > 0
                    ? devices.filter(d => selectedPowerMeters.includes(d.deviceId)).reduce((sum, d) => sum + (d.watt || 0), 0)
                    : stats.totalPower;
                  const displayValue = selectedPower >= 1000
                    ? (selectedPower / 1000).toFixed(1)
                    : selectedPower.toFixed(0);
                  const unit = selectedPower >= 1000 ? 'kW' : 'W';
                  const valueLength = displayValue.length;
                  const fontSize = valueLength <= 3 ? 'text-5xl' : valueLength <= 5 ? 'text-4xl' : 'text-3xl';
                  const unitSize = valueLength <= 3 ? 'text-lg' : valueLength <= 5 ? 'text-base' : 'text-sm';
                  return (
                    <div className="text-center">
                      <p className={`${fontSize} font-bold text-yellow-500 leading-none`}>{displayValue}</p>
                      <p className={`${unitSize} text-yellow-500 font-normal`}>{unit}</p>
                    </div>
                  );
                })()}
              </div>
            </div>
            <p className="text-base font-bold text-slate-400 text-center mt-1">即時總功率</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-400 mt-1">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-cyan-500"></span>
                空調
                <span className="font-bold text-white ml-1">
                  {typeof stats.totalPower === 'number' && stats.totalPower > 0 && typeof stats.hvacPower === 'number'
                    ? `${((stats.hvacPower / stats.totalPower) * 100).toFixed(0)}%`
                    : '0%'}
                </span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                照明
                <span className="font-bold text-white ml-1">
                  {typeof stats.totalPower === 'number' && stats.totalPower > 0 && typeof stats.lightingPower === 'number'
                    ? `${((stats.lightingPower / stats.totalPower) * 100).toFixed(0)}%`
                    : '0%'}
                </span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-slate-400"></span>
                其他
                <span className="font-bold text-white ml-1">
                  {stats.totalPower > 0 ? `${((stats.otherPower / stats.totalPower) * 100).toFixed(0)}%` : '0%'}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Three-Phase Meter & Energy Ranking Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 auto-rows-fr items-stretch flex-1 min-h-[260px]">
        {/* Three-Phase Meter */}
        <div className="bg-[#0C2340] rounded-xl shadow-sm border border-slate-700/30 p-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-purple-500" />
              <h3 className="text-lg font-semibold text-white">三相電錶即時監測</h3>
            </div>
            {hasAvailableMeters && (
              <button
                onClick={openThreePhaseMeterSelector}
                className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                {threePhaseMeter ? threePhaseMeter.deviceName : '點擊選擇電錶'}
                <Settings2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {!hasAvailableMeters ? (
            <div className="flex flex-1 min-h-[200px] flex-col items-center justify-center text-slate-400">
              <AlertCircle className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">你的系統中沒有可用設備</p>
              <p className="text-xs text-slate-500 mt-1">請先在設備管理中匯入三相電錶</p>
            </div>
          ) : !selectedThreePhaseMeter ? (
            <div className="flex flex-1 min-h-[200px] flex-col items-center justify-center text-slate-400">
              <Activity className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">請先選擇一個三相電錶</p>
              <button
                onClick={openThreePhaseMeterSelector}
                className="mt-2 px-3 py-1.5 bg-purple-900/30 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-900/50 transition-colors"
              >
                選擇電錶
              </button>
            </div>
          ) : threePhaseData ? (
            <div className="grid grid-cols-3 gap-2 flex-1 min-h-[200px] items-center justify-items-center">
              <PhaseGauge phase="R" power={threePhaseData.R.power} voltage={threePhaseData.R.voltage} current={threePhaseData.R.current} maxPower={maxPower} size={200} />
              <PhaseGauge phase="S" power={threePhaseData.S.power} voltage={threePhaseData.S.voltage} current={threePhaseData.S.current} maxPower={maxPower} size={200} />
              <PhaseGauge phase="T" power={threePhaseData.T.power} voltage={threePhaseData.T.voltage} current={threePhaseData.T.current} maxPower={maxPower} size={200} />
            </div>
          ) : (
            <div className="flex flex-1 min-h-[200px] flex-col items-center justify-center text-slate-400">
              <Activity className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">無法取得數據</p>
            </div>
          )}
        </div>

        {/* Energy Ranking & Carbon Footprint */}
        <div className="bg-[#0C2340] rounded-xl shadow-sm border border-slate-700/30 p-4 flex flex-col h-full">
          <div className="grid grid-cols-2 gap-4 h-full">
            {/* Energy Ranking */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-yellow-500" />
                  <h3 className="text-xl font-semibold text-white">過去一周能耗排行</h3>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={fetchWeeklyEnergyRanking}
                    disabled={isLoadingWeeklyEnergy}
                    className="p-1 text-slate-400 hover:text-slate-300 disabled:opacity-50"
                    title="重新載入"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoadingWeeklyEnergy ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => {
                      setTempExcludedRankingDevices([...excludedRankingDevices]);
                      setShowExcludeRankingModal(true);
                    }}
                    className="p-1 text-slate-400 hover:text-slate-300"
                    title="排除設備"
                  >
                    <Settings2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {allEnergyDevices.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm">
                  <AlertCircle className="w-8 h-8 mb-2 opacity-50" />
                  <p>你的系統中沒有可用設備</p>
                  <p className="text-xs text-slate-500 mt-1">請先在設備管理中匯入電錶</p>
                </div>
              ) : top5.length === 0 ? (
                isLoadingWeeklyEnergy ? (
                  <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    載入中...
                  </div>
                ) : Object.keys(weeklyEnergyPerDevice).length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                    暫無數據，請點擊刷新按鈕載入
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                    暫無數據
                  </div>
                )
              ) : (
                <div className="flex-1 space-y-2">
                  {top5.map((device: any, index: number) => {
                    const energyKWh = device.weeklyEnergyKWh;
                    const percentage = weeklyTotalEnergy > 0 ? (energyKWh / weeklyTotalEnergy) * 100 : 0;
                    const progress = weeklyTotalEnergy > 0 ? energyKWh / weeklyTotalEnergy : 0;
                    return (
                      <div
                        key={device.deviceId}
                        onClick={() => setSelectedDevice(device)}
                        className="cursor-pointer hover:bg-slate-700/50 rounded-lg p-2 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-white">
                            {index + 1}. {device.deviceName}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-yellow-500">
                              {energyKWh.toFixed(2)} kWh
                            </span>
                            <span className="text-xs text-slate-400">
                              ({percentage.toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-slate-800/50 rounded-full h-2">
                          <div
                            className="bg-yellow-500 h-2 rounded-full transition-all"
                            style={{ width: `${progress * 100}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Carbon Footprint */}
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center gap-3 mb-6">
                <Leaf className="w-8 h-8 text-green-500" />
                <h3 className="text-xl font-semibold text-white">今日碳足跡</h3>
              </div>
              <div className="text-center">
                <div className="mb-6">
                  <p className="text-5xl font-bold text-white mb-2">
                    {carbonFootprint.toFixed(2)}
                  </p>
                  <p className="text-xl font-medium text-slate-400">kg CO2</p>
                </div>
                <div className="flex items-center justify-center gap-3 text-green-400">
                  <span className="text-4xl">🌳</span>
                  <span className="text-lg font-medium">約 {treeEquivalent} 棵樹吸收量</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 auto-rows-fr items-stretch flex-1 min-h-[260px]">
        {/* Today's Power Chart */}
        <div className="bg-[#0C2340] rounded-xl shadow-sm border border-slate-700/30 p-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Zap className={`w-5 h-5 text-yellow-500 ${isLoadingChartData ? 'animate-pulse' : ''}`} />
              <h3 className="text-lg font-semibold text-white">
                {t.intelliSpace?.todayPowerConsumption || '本日用電曲線'}
              </h3>
              <div className="relative group inline-flex items-center">
                <Info className="w-4 h-4 text-slate-400 cursor-help hover:text-slate-300 transition-colors" />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none whitespace-nowrap">
                  <div className="bg-slate-800/50 text-white text-xs rounded-lg py-2 px-3 shadow-xl border border-slate-700/50">
                    <div className="text-center">
                      <div className="font-medium mb-1 text-white">時間粒度說明</div>
                      <div className="text-slate-300">00:00 代表 00:00 到 01:00 的用電量加總</div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-slate-700"></div>
                  </div>
                </div>
              </div>
              {isLoadingChartData && <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />}
              {/* 統計摘要 */}
              {chartStats.todayPeakPower > 0 && (
                <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-0.5 rounded">
                  峰值 {formatEnergyStats(chartStats.todayPeakPower)}
                </span>
              )}
            </div>
            <span className="text-xs text-slate-400">
              {selectedPowerMeters.length > 0 
                ? `${selectedPowerMeters.length} ${t.intelliSpace?.metersSelected || '電錶已選'}`
                : `${stats.powerMeterCount} ${t.intelliSpace?.meters || '電錶'}`}
            </span>
          </div>
          <div className="flex-1 min-h-[220px]">
            <PowerChart 
              data={powerChartData} 
              todayLabel={t.intelliSpace?.today || '今日'} 
              yesterdayLabel={t.intelliSpace?.yesterday || '昨日'}
              lastWeekLabel={t.intelliSpace?.lastWeek || '上周'}
            />
          </div>
          <div className="mt-3 flex justify-center items-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-yellow-500 rounded"></span>
              <span>{t.intelliSpace?.today || '今日'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-gray-300 rounded"></span>
              <span>{t.intelliSpace?.yesterday || '昨日'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-blue-500 rounded" style={{ borderTop: '1px dashed #3b82f6' }}></span>
              <span>{t.intelliSpace?.lastWeek || '上周'}</span>
            </span>
          </div>
        </div>

        {/* Monthly Power Chart */}
        <div className="bg-[#0C2340] rounded-xl shadow-sm border border-slate-700/30 p-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Zap className={`w-5 h-5 text-yellow-500 ${isLoadingChartData ? 'animate-pulse' : ''}`} />
              <h3 className="text-lg font-semibold text-white">本月用電曲線</h3>
              {isLoadingChartData && <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />}
              {/* 統計摘要 */}
              {chartStats.monthPeakPower > 0 && (
                <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-0.5 rounded">
                  峰值 {formatEnergyStats(chartStats.monthPeakPower)}
                </span>
              )}
            </div>
            <span className="text-xs text-slate-400">
              {selectedPowerMeters.length > 0 
                ? `${selectedPowerMeters.length} ${t.intelliSpace?.metersSelected || '電錶已選'}`
                : `${stats.powerMeterCount} ${t.intelliSpace?.meters || '電錶'}`}
            </span>
          </div>
          <div className="flex-1 min-h-[220px]">
            <MonthlyPowerChart data={monthlyChartData} currentMonthLabel="本月" lastMonthLabel="上月" />
          </div>
          <div className="mt-3 flex justify-center items-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-yellow-500 rounded"></span>
              <span>本月</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-gray-300 rounded"></span>
              <span>上月</span>
            </span>
          </div>
        </div>

        {/* Climate Chart */}
        <div className="bg-[#0C2340] rounded-xl shadow-sm border border-slate-700/30 p-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Thermometer className={`w-5 h-5 text-orange-500 ${isLoadingChartData ? 'animate-pulse' : ''}`} />
              <h3 className="text-lg font-semibold text-white">
                {t.intelliSpace?.todayClimate || '本日溫濕度曲線'}
              </h3>
              {isLoadingChartData && <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />}
              {/* 統計摘要 - 溫度變化幅度 */}
              {chartStats.tempRange !== null && (
                <span className="text-xs text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded">
                  變化 {chartStats.tempRange.toFixed(1)}°C
                </span>
              )}
            </div>
            <span className="text-xs text-slate-400">
              {selectedThSensors.length > 0 
                ? `${selectedThSensors.length} 感測器已選`
                : `${stats.thSensorCount} ${t.intelliSpace?.sensors || '感測器'}`}
            </span>
          </div>
          <div className="flex-1 min-h-[220px]">
            <ClimateChart 
              data={climateChartData} 
              hasData={hasClimateChartData}
              realtimeTemp={stats.avgTemp}
              realtimeHumidity={stats.avgHumidity}
            />
          </div>
          {hasClimateChartData && (
            <div className="mt-3 flex justify-center items-center gap-6 text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-0.5 bg-blue-500 rounded"></span>
                <span>濕度 (%)</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-0.5 bg-orange-500 rounded"></span>
                <span>溫度 (°C)</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnergyView;
